import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface ProfileResponse {
  success: boolean;
  profile?: { proxyWallet?: string };
}

interface DataAPIPosition {
  proxyWallet: string;
  asset: string;
  conditionId: string;
  size: number;
  avgPrice: number;
  curPrice: number;
  initialValue: number;
  currentValue: number;
  cashPnl: number;
  percentPnl: number;
  title: string;
  slug: string;
  icon: string;
  outcome: string;
  endDate: string;
  eventSlug: string;
}

/** Resolve a username or address to a Polymarket proxy wallet via our API route */
async function resolveUser(user: string): Promise<string | null> {
  if (user.startsWith("0x") && user.length === 42) return user;

  const res = await fetch(`/api/polymarket/profile?username=${encodeURIComponent(user)}`);
  if (!res.ok) return null;
  const data = (await res.json()) as ProfileResponse;
  return data.profile?.proxyWallet ?? null;
}

/**
 * Fetch recent positions for a Polymarket user via the Data API.
 * Uses our Next.js API proxy for username resolution (CORS),
 * then fetches positions from the Data API server-side.
 */
export const polymarketUserBetFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { triggerPayload } = ctx;
  const user = triggerPayload.user as string | undefined;
  if (!user) return [];

  const address = await resolveUser(user);
  if (!address) return [];

  // Fetch positions via our proxy to avoid CORS
  const res = await fetch(`/api/polymarket/user-positions?${new URLSearchParams({ user: address, limit: "5" })}`);
  if (!res.ok) return [];
  const data = (await res.json()) as { success: boolean; positions?: DataAPIPosition[] };
  if (!data.success) return [];

  const positions = data.positions ?? [];

  return positions.map(p => ({
    triggeredAt: new Date().toISOString(),
    trade: {
      id: p.asset,
      tokenId: p.asset,
      marketId: p.conditionId,
      question: p.title,
      side: "BUY",
      outcome: p.outcome,
      price: p.curPrice,
      size: p.size,
      amount: p.currentValue,
      makerAddress: address,
      timestamp: new Date().toISOString(),
    },
    _question: p.title,
    _outcome: p.outcome,
    _size: p.size,
    _price: p.curPrice,
    _pnl: p.cashPnl,
    _icon: p.icon,
  }));
};
